import type { Choice, Device, Feature, FeatureId, Question, QuestionId, Workflow } from '~/utils/content-types';

export const isDirectlyPresent = (feature: Feature, device: Device): boolean =>
  device.data.features.some((x) => x.id === feature.id);

export const isIndirectlyPresent = (feature: Feature, device: Device): boolean =>
  device.data.features.flatMap((x) => x.data.included).some((x) => x.id === feature.id);

export const isPresent = (feature: Feature, device: Device): boolean =>
  isDirectlyPresent(feature, device) || isIndirectlyPresent(feature, device);

export const getChoices = (
  workflow: Workflow,
  selected: Set<FeatureId>
): {
  question: Question;
  choice: Choice;
}[] =>
  workflow.data.questions
    .flatMap((question) => question.data.choices.map((choice) => ({ question, choice })))
    .filter((x) => x.choice.features.length > 0)
    .filter((x) => x.choice.features.some((y) => selected.has(y.id)));

export const getFeatures = (selected: Map<QuestionId, Feature[]>): Feature[] =>
  Array.from(selected.values()).flatMap((x) => x);

export const getFeatureIds = (selected: Map<QuestionId, Feature[]>): Set<FeatureId> =>
  new Set(getFeatures(selected).map((x) => x.id));

export const getHardFilterFeatures = (workflow: Workflow, selected: Set<FeatureId>): Feature[] =>
  getChoices(workflow, selected)
    .filter((x) => x.question.data.filter === 'hard')
    .flatMap((x) => x.choice.features);

export const getMaxPoints = (workflow: Workflow, selected: Set<FeatureId>): number =>
  getChoices(workflow, selected)
    .map((x) => x.choice.points)
    .reduce((acc, x) => acc + x, 0);

export const getDevicePoints = (workflow: Workflow, selected: Set<FeatureId>, device: Device): number =>
  getChoices(workflow, selected).reduce(
    (acc, { choice: { points, features } }) =>
      // if feature is found in the device, add points
      features.some((feature) => isDirectlyPresent(feature, device))
        ? acc + points
        : // if feature is found in included features, add a bit less points, but close to maximum
          // that way we don't lose a star, but preferred device would always be the one with the exact same features
          device.data.features.some((feature) => isIndirectlyPresent(feature, device))
          ? acc + points * 0.95
          : // if feature is not found, don't add
            acc,
    0
  );

export const getDeviceMatch = (workflow: Workflow, selected: Set<FeatureId>, device: Device): number | undefined => {
  const maxPoints = getMaxPoints(workflow, selected);

  if (!maxPoints) return undefined;

  const points = getDevicePoints(workflow, selected, device);

  return points / maxPoints;
};

export const getUniqueNamedFeatures = (workflow: Workflow, selected: Set<FeatureId>): Map<string, Feature[]> =>
  getChoices(workflow, selected)
    .flatMap((x) => x.choice.features)
    .reduce((acc, feature) => {
      const key = feature.data.title;

      if (!acc.has(key)) return acc.set(key, [feature]);

      const features = acc.get(key)!;

      if (!features.some((x) => x.id === feature.id)) {
        features.push(feature);
        acc.set(key, features);
      }

      return acc;
    }, new Map<string, Feature[]>());

export const getMatchedDevices = (
  workflow: Workflow,
  selected: Set<FeatureId>,
  devices: Device[]
): (Device & { match?: number })[] =>
  devices
    .filter((device) => device.data.categories.some((category) => category.id === workflow.data.category.id))
    .map((device) => ({
      ...device,
      match: getDeviceMatch(workflow, selected, device),
    }));

export const getTopDevices = (workflow: Workflow, selected: Set<FeatureId>, devices: Device[]): Device[] => {
  const sortedDevices = getMatchedDevices(workflow, selected, devices).sort((a, b) => (b.match ?? 0) - (a.match ?? 0));
  const hardFilterFeatures = getHardFilterFeatures(workflow, selected);

  return sortedDevices
    .filter((x) => !!x.match && !isNaN(x.match))
    .filter((device) => hardFilterFeatures.every((feature) => isPresent(feature, device)))
    .slice(0, 3);
};
